import { DEFAULT_NS, DEFAULT_ID } from './types.js';

/**
 * Минимальный реестр UI-команд.
 *
 * Основные принципы:
 *  - Один провайдер обслуживает ровно один namespace.
 *  - id провайдера опционален, по умолчанию = DEFAULT_ID.
 *  - Уникальность провайдера определяется как namespace.id.
 *  - Для каждого namespace хранится список провайдеров, отсортированный по:
 *      1) приоритету (по убыванию),
 *      2) времени регистрации (по возрастанию).
 *  - Если у провайдера defaultNSAllowed = true — он дополнительно
 *    регистрируется в DEFAULT_NS (приоритет defaultNDPriority ?? 0).
 */
class UiCommandRegistry {
    constructor() {
        /** namespace → список провайдеров (всегда отсортированный) */
        this.buckets = new Map();
    }
    /** Приведение namespace к единому виду */
    normNs(ns) { return (ns ?? DEFAULT_NS).toLowerCase(); }
    /** Если id не задан → вернуть DEFAULT_ID */
    idOrDefault(id) { return id || DEFAULT_ID; }
    /** Сборка уникального ключа для провайдера */
    makeKey(ns, idInNs) { return `${ns}.${idInNs}`; }
    /**
     * Вставить или заменить провайдера в список с поддержанием сортировки.
     * Сортировка: priority ↓, при равенстве — ts ↑.
     */
    insertSorted(list, meta) {
        const idx = list.findIndex(x => x.idInNs === meta.idInNs);
        if (idx >= 0) {
            list[idx] = meta; // обновляем существующего
        }
        else {
            list.push(meta);
        }
        if (list.length > 1) {
            list.sort((a, b) => (b.priority - a.priority) || (a.ts - b.ts));
        }
    }
    /**
     * Регистрация провайдера.
     * Если провайдер с тем же namespace.id уже был — заменяется.
     *
     * Приоритет берём из свойств провайдера:
     *  - основной ns: provider.priority ?? 0
     *  - DEFAULT_NS (если разрешено): provider.defaultNDPriority ?? 0
     */
    registerProvider(provider) {
        const now = Date.now();
        // Основной namespace
        const primaryNs = this.normNs(provider.namespace);
        const idInNs = this.idOrDefault(provider.id);
        const primaryPriority = provider.priority ?? 0;
        const metaPrimary = {
            ns: primaryNs,
            key: this.makeKey(primaryNs, idInNs),
            provider,
            idInNs,
            priority: primaryPriority,
            ts: now,
        };
        const listPrimary = this.buckets.get(primaryNs) ?? [];
        this.insertSorted(listPrimary, metaPrimary);
        this.buckets.set(primaryNs, listPrimary);
        // Доп. регистрация в DEFAULT_NS, если разрешено
        if (provider.defaultNSAllowed) {
            const defaultNs = DEFAULT_NS;
            const ndPriority = provider.defaultNDPriority ?? primaryPriority;
            const metaDefault = {
                ns: defaultNs,
                key: this.makeKey(defaultNs, idInNs),
                provider,
                idInNs,
                priority: ndPriority,
                ts: now,
            };
            const listDefault = this.buckets.get(defaultNs) ?? [];
            this.insertSorted(listDefault, metaDefault);
            this.buckets.set(defaultNs, listDefault);
        }
    }
    /**
     * Удаление провайдера по namespace и id.
     * Если id не задан → считается DEFAULT_ID.
     * Дополнительно пытаемся убрать копию из DEFAULT_NS (если это не он).
     */
    unregisterProvider(nsRaw, id) {
        const ns = this.normNs(nsRaw);
        const idInNs = this.idOrDefault(id);
        const removeFrom = (bucketNs) => {
            const list = this.buckets.get(bucketNs);
            if (!list)
                return;
            const idx = list.findIndex(x => x.idInNs === idInNs);
            if (idx >= 0)
                list.splice(idx, 1);
            if (list.length === 0)
                this.buckets.delete(bucketNs);
        };
        removeFrom(ns);
        if (ns !== this.normNs(DEFAULT_NS)) {
            removeFrom(this.normNs(DEFAULT_NS));
        }
    }
    /** Вернуть первого (лучшего) провайдера для namespace */
    first(ns) {
        const list = this.buckets.get(ns);
        return list?.[0];
    }
    /**
     * Разрешение токена без исполнения.
     * Возвращает провайдера, который будет выбран для данного namespace.
     */
    resolve(token) {
        const ns = this.normNs(token.namespace);
        const winner = this.first(ns);
        return winner ? { providerId: winner.idInNs, provider: winner.provider } : null;
        // (Опционально можно добавить диагностику fallback в DEFAULT_NS,
        //  но resolve по контракту отдаёт победителя конкретного ns)
    }
    /**
     * Исполнение токена:
     *   - перебор провайдеров основного namespace по приоритету,
     *     каждый может обработать часть targetItems;
     *   - если остались необработанные targetItems ИЛИ никто не обработал,
     *     делаем fallback к DEFAULT_NS (если он отличен от исходного),
     *     также пробуем «доесть» остаток.
     *
     * Возвращает:
     *   - null, если совсем ничего не обработалось,
     *   - объект или массив объектов результатов (совместимо с прежней сигнатурой).
     */
    runToken(token, ctx) {
        const acc = [];
        const norm = (ns) => (ns ?? DEFAULT_NS).toLowerCase();
        // флаг: есть ли кооперативный режим (только при наличии targetItems)
        const cooperative = Array.isArray(token.targetItems);
        const pushRes = (res) => {
            if (res === true) {
                acc.push({});
            }
            else if (Array.isArray(res)) {
                for (const r of res)
                    acc.push(r);
            }
            else {
                acc.push(res);
            }
        };
        const tryNamespace = (ns) => {
            const list = this.buckets.get(ns);
            if (!list || list.length === 0)
                return false;
            let hadAny = false;
            for (const meta of list) {
                try {
                    // если есть таргеты и они уже закончились — прекращаем проход по ns
                    if (cooperative && token.targetItems && token.targetItems.length === 0)
                        break;
                    const res = meta.provider.execute(token, ctx);
                    if (res === false)
                        continue;
                    hadAny = true;
                    // === поведение без targetItems (legacy-режим) ===
                    if (!cooperative) {
                        if (res === true) {
                            acc.push({});
                            return true;
                        }
                        if (Array.isArray(res)) {
                            acc.push(...res);
                            return true;
                        }
                        acc.push(res);
                        return true;
                    }
                    // Кооперативный режим: аккумулируем и идём дальше,
                    // ожидая что провайдеры «съедают» часть token.targetItems.
                    // Это происходит в executeOnItems
                    pushRes(res);
                }
                catch (e) {
                    console.error('[UiCommandRegistry] Ошибка при обработке команды', e);
                }
            }
            return hadAny;
        };
        const primaryNs = norm(token.namespace);
        const hadOnPrimary = tryNamespace(primaryNs);
        const needFallback = primaryNs !== norm(DEFAULT_NS) &&
            (
            // остались не обработанные цели
            (cooperative && token.targetItems && token.targetItems.length > 0) ||
                // никто не обработал в primary
                !hadOnPrimary);
        //console.log('needFallback', needFallback, 'primaryNs', primaryNs, 'hadOnPrimary', hadOnPrimary);
        if (needFallback) {
            tryNamespace(norm(DEFAULT_NS));
        }
        if (acc.length === 0)
            return null;
        return cooperative ? acc : (acc.length === 1 ? acc[0] : acc);
    }
    /**
     * Служебная диагностика: показать список зарегистрированных провайдеров.
     * Можно фильтровать по namespace.
     */
    list(filter) {
        const out = [];
        if (filter?.namespace) {
            const ns = this.normNs(filter.namespace);
            (this.buckets.get(ns) ?? []).forEach(m => out.push({ namespace: ns, providerId: m.idInNs, priority: m.priority, ts: m.ts }));
        }
        else {
            this.buckets.forEach((list, ns) => list.forEach(m => out.push({ namespace: ns, providerId: m.idInNs, priority: m.priority, ts: m.ts })));
        }
        return out;
    }
}
/** Глобальный синглтон (по желанию можно не использовать) */
UiCommandRegistry.uiCommands = new UiCommandRegistry();

export { UiCommandRegistry };
